import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Form } from 'antd';
import {
  DarwinButton,
  DarwinDivider,
  DarwinFlex,
  DarwinInput,
  DarwinSelect,
  DarwinSpin,
  DarwinTreeSelect,
} from 'components/darwin-ui';
import RadioToggle from 'components/darwin-ui/RadioToggle';
import { useTranslation } from 'react-i18next';
import styles from '../ProcessBlock.module.css';
import { TabSaveHandler } from '../';
import { useGetUserByAllCompanies } from 'api/services/userService';
import { extractQueryData } from 'utils/services';
import { transformToCompanyTree } from 'components/ui/roles/EditRoles/TreeSection/utils';
import {
  useCreateNewForm,
  useGetFormTemplates,
  useGetTemplateFields,
} from 'api/services/dataCaptureService';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ROUTES } from 'routes/routes';
import { ErrorType } from 'types/genericTypes';
import {
  ProcessFormTemplateField,
  DataFieldCols,
  ProcessFormNewFormPayload,
} from 'types/dataCaptureTypes';
import { FormConfigurationField } from './ConfigurationForm/configs/types';
import FormCapture from './Tabs/FormCapture';
import { getFieldOverride } from './formPayloadHelper';
import {
  getFormConfigurationFields,
  FieldContext,
  isSpecialField,
} from './ConfigurationForm/configs/fieldConfigurations';
import { FieldTypeKeys } from './ConfigurationForm/configs/types';
import style from './index.module.css';
import { useUI } from 'contexts/UiContext';
import {
  useGetProcessBlockById,
  useUpdateProcessBlockId,
} from 'api/services/processMapService';
import { useGetAllDataSourcesByCompany } from 'api/services/dataSourceService';
import { ConfigurationTabs } from 'types/processBlockTypes';
import { ActionsBar } from './Tabs/FlatFile/ActionsBar';
import FlatFile from './Tabs/FlatFile';
import { DataSourcesListView } from 'types/dataSourcesTypes';
import { useQueryClient } from '@tanstack/react-query';

const { Option } = DarwinSelect;

interface CompanyTreeNode {
  key: number | string;
  title: string;
  children?: CompanyTreeNode[];
}

/** Transforms a company tree into the shape expected by DarwinTreeSelect */
const transformTreeData = (nodes: CompanyTreeNode[]): any[] =>
  nodes.map(node => ({
    value: String(node.key),
    title: node.title,
    children: node.children ? transformTreeData(node.children) : undefined,
  }));

const SETTINGS_FIELD_MAX_WIDTH = 448;
const FORM_VERSION_MAX_LENGTH = 50;

export const ConfigureDataCapture = forwardRef<TabSaveHandler>((props, ref) => {
  const [form] = Form.useForm();
  const [formflatFileIntegration] = Form.useForm();
  const { t } = useTranslation();
  const { processId: processFlowId, id: traceabilityPlanId } = useParams<{
    processId: string;
    id: string;
  }>();
  const { openNotification } = useFloatNotification();
  const navigate = useNavigate();
  const selectedForm = Form.useWatch('form', form);
  const { selectedCompany } = useUI();
  const queryClient = useQueryClient();

  const processBlockRes = useGetProcessBlockById({
    companyId: selectedCompany || '',
    traceabilityPlanId: traceabilityPlanId || '',
    processFlowId: processFlowId || '',
  });

  const dataSourcesReq = useGetAllDataSourcesByCompany({
    companyId: selectedCompany || '',
    page: 1,
    limit: 10,
    sortOrder: { sortBy: 'name', order: 'ASC' },
  });

  const processBlock = extractQueryData(processBlockRes, undefined);
  const dataSourcesData = extractQueryData<DataSourcesListView[]>(
    dataSourcesReq,
    []
  );

  const [dataSourceSelectedId, setDataSourceSelectedId] = useState<string>('');

  const integrationOptions = useMemo(
    () => [
      { value: '', label: t('process-flow.data-capture.predefined') },
      ...(dataSourcesData || []).map(dataSource => ({
        value: dataSource.id,
        label: dataSource.name,
      })),
    ],
    [dataSourcesData, t]
  );

  const processForm = processBlock?.processForm;
  const formVersionId = processForm?.form_id;

  // Validate the form name with
  const formNameVersion = useMemo(() => {
    return processForm
      ? processForm.form_name
      : `${processBlock?.plan.name} ${processBlock?.name}`;
  }, [processForm, processBlock?.plan.name, processBlock?.name]);

  const [formAutoPopulateName, setFormAutoPopulateName] =
    useState(formNameVersion);

  useEffect(() => {
    setFormAutoPopulateName(formNameVersion);
  }, [formNameVersion]);

  const templateId = processForm?.templateId;
  const fields = processForm?.fields;
  const defaultUserIds = processForm?.userIds;

  // get users by process and put into treeData
  const processId = processBlock?.process.id;
  const usersQueryByProcess = useGetUserByAllCompanies(processId ?? '');
  const usersByProcess = extractQueryData(usersQueryByProcess, []);
  const userCompanyTree = (
    transformToCompanyTree(usersByProcess, false, true) as CompanyTreeNode[]
  ).map(node => ({ ...node, key: String(node.key) }));
  const userTreeDataByAllCompanies = transformTreeData(userCompanyTree);
  const userIds = (Form.useWatch('userIds', form) as string[]) || undefined;
  const templatesQuery = useGetFormTemplates(processFlowId);
  const fieldsQuery = useGetTemplateFields(
    selectedForm,
    traceabilityPlanId,
    processFlowId
  );
  const { mutateAsync: createNewForm } = useCreateNewForm();

  const formTemplates = extractQueryData(templatesQuery, []);
  const dataFields = extractQueryData(fieldsQuery, undefined);
  const isLoadingFields = templatesQuery.isLoading || fieldsQuery.isLoading;
  const showTable = !!selectedForm;

  // Create field context for configuration
  const fieldContext: FieldContext = useMemo(
    () => ({
      formTemplates,
      integrationOptions,
      t,
      settingsFieldMaxWidth: SETTINGS_FIELD_MAX_WIDTH,
      onDataSourceSelect: (value: string) => setDataSourceSelectedId(value),
    }),
    [formTemplates, integrationOptions, t]
  );

  // Auto-fill the "Form" select if there is exactly one option and no prefilled value.
  useLayoutEffect(() => {
    if (!templateId && formTemplates.length === 1) {
      form.setFieldsValue({ form: formTemplates[0].form_id });
    }
  }, [templateId, formTemplates, form]);

  const formConfigurationFields: FormConfigurationField[] = useMemo(
    () =>
      getFormConfigurationFields(ConfigurationTabs.FormCapture, fieldContext),
    [fieldContext]
  );

  const formIntegrationFields: FormConfigurationField[] = useMemo(
    () => getFormConfigurationFields(ConfigurationTabs.FlatFile, fieldContext),
    [fieldContext]
  );

  const handleNavigateForm = useCallback(() => {
    navigate(
      generatePath(ROUTES.DATA.CAPTURE_FORMS.EDIT, {
        id: dataFields?.form_id || '',
        traceability: traceabilityPlanId || '',
        processFlow: processFlowId || '',
      })
    );
  }, [dataFields?.form_id, navigate, processFlowId, traceabilityPlanId]);

  const [captureFields, setCaptureFields] = useState<DataFieldCols[]>([]);
  useEffect(() => {
    if (dataFields?.fields || fields) {
      const customFields = fields || dataFields?.fields || [];

      const mapped = customFields.map((field: ProcessFormTemplateField) => {
        const {
          accepted_values,
          is_required,
          is_visible,
          flow_through,
          field_type,
          is_editable,
        } = field.metadata;
        const fieldKey = field.field_key;
        const values =
          field?.metadata?.values?.map((v: any) => v.entityId) || [];
        const defaultValue =
          field?.metadata?.default_values?.value || undefined;
        return {
          key: field.id,
          name: field.name,
          required: is_required,
          visible: is_visible,
          flowThrough: flow_through,
          systemGenerated: field.metadata.system_generated,
          field_type,
          accepted_values,
          acceptedValues: values,
          defaultValue: defaultValue,
          default_values: field.metadata.default_values,
          field_key: fieldKey,
          is_custom: field.is_custom,
          is_editable,
        } as DataFieldCols;
      });
      setCaptureFields(mapped);
    }
  }, [dataFields, fields]);

  const onFieldChange = useCallback(
    <T extends keyof DataFieldCols>(
      key: string,
      field: T,
      newValue: DataFieldCols[T]
    ) => {
      setCaptureFields(prev =>
        prev.map(item => {
          if (item.key !== key) return item;
          if (field === 'acceptedValues') {
            const newAcceptedValues = newValue as string[];
            return {
              ...item,
              acceptedValues: newAcceptedValues,
              defaultValue:
                newAcceptedValues.length === 1
                  ? newAcceptedValues[0]
                  : undefined,
            };
          }
          return { ...item, [field]: newValue };
        })
      );
    },
    []
  );

  const renderInput = useCallback(
    (field: FormConfigurationField) => {
      if (isSpecialField(field.name)) {
        if (field.name === FieldTypeKeys.USER_IDS) {
          return (
            <DarwinTreeSelect
              treeNodeFilterProp="title"
              treeData={userTreeDataByAllCompanies}
              placeholder={field.placeholder}
              treeCheckable
              style={{ width: '100%' }}
            />
          );
        } else if (field.name === FieldTypeKeys.FORM_VERSION) {
          return (
            <DarwinFlex align="center">
              <DarwinInput
                placeholder={field.placeholder}
                value={formAutoPopulateName}
                maxLength={FORM_VERSION_MAX_LENGTH}
                style={{ width: '100%', maxWidth: SETTINGS_FIELD_MAX_WIDTH }}
                onChange={e => setFormAutoPopulateName(e.target.value)}
              />
              {/* Only show edit button when form was previously saved */}
              {processForm && (
                <DarwinButton
                  className={style.editButton}
                  onClick={handleNavigateForm}
                  type="link"
                >
                  {t('edit')}
                </DarwinButton>
              )}
            </DarwinFlex>
          );
        } else if (field.name === FieldTypeKeys.EXTERNAL_CODE) {
          return (
            <DarwinInput
              type="number"
              min={1}
              max={999}
              placeholder={field.placeholder}
              maxLength={FORM_VERSION_MAX_LENGTH}
              style={{ width: '100%', maxWidth: SETTINGS_FIELD_MAX_WIDTH }}
              className="no-arrows"
            />
          );
        }
      }

      // Default select rendering for non-special fields
      return (
        <DarwinSelect placeholder={field.placeholder} onSelect={field.onSelect}>
          {field.options?.map(opt => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </DarwinSelect>
      );
    },
    [
      userTreeDataByAllCompanies,
      handleNavigateForm,
      t,
      formAutoPopulateName,
      processForm,
    ]
  );

  const handleFinish = useCallback(
    async (
      values: { form: string; formVersion: string },
      saveAndExit?: boolean
    ) => {
      const { form: templateId } = values;
      const templateName = formAutoPopulateName || '';
      const fieldOverrides = captureFields
        .map(field => getFieldOverride(field, t))
        .filter(Boolean);
      const payload: ProcessFormNewFormPayload = {
        templateId,
        blockId: String(processFlowId),
        name: templateName,
        userIds,
        fieldOverrides: fieldOverrides,
      };

      try {
        await createNewForm({ payload });
        openNotification(
          t('success'),
          t('process-flow.data-capture.create-success'),
          'success'
        );

        queryClient.invalidateQueries({ queryKey: ['process-block-by-id'] });

        if (saveAndExit) {
          navigate(
            generatePath(ROUTES.PROCESSES.MAPS.MAP.EDIT, {
              id: traceabilityPlanId || '',
            })
          );
        }
      } catch (err) {
        const apiError = err as ErrorType;
        const errorMessage = apiError.body?.errorCode
          ? t(apiError.body.errorCode)
          : t('process-flow.data-capture.create-error');
        openNotification(t('error'), errorMessage, 'error');
      }
    },
    [
      captureFields,
      createNewForm,
      navigate,
      openNotification,
      processFlowId,
      t,
      traceabilityPlanId,
      formAutoPopulateName,
      userIds,
      queryClient,
    ]
  );

  const {
    mutateAsync: updateProcessBlock,
    isPending: isUpdateProcessBlockPending,
  } = useUpdateProcessBlockId();

  const handleFinishFlatFileForm = useCallback(
    async (
      values: { formflatFileIntegration: string; externalCode: string },
      saveAndExit?: boolean
    ) => {
      try {
        const n = values.externalCode ? Number(values.externalCode) : undefined;

        if (
          n !== undefined &&
          (Number.isNaN(n) || n < 1 || n > 999 || !Number.isInteger(n))
        ) {
          openNotification(
            t('error'),
            t('process-flow.data-capture.external-code-out-of-range'),
            'error'
          );
          return;
        }

        await updateProcessBlock({
          companyId: selectedCompany || '',
          traceabilityPlanId: traceabilityPlanId || '',
          processFlowId: processFlowId || '',
          payload: {
            externalCode: n,
          },
        });
        openNotification(
          t('success'),
          t('process-flow.data-capture.create-success'),
          'success'
        );

        if (saveAndExit) {
          navigate(
            generatePath(ROUTES.PROCESSES.MAPS.MAP.EDIT, {
              id: traceabilityPlanId || '',
            })
          );
        }
      } catch (err) {
        const apiError = err as ErrorType;

        const raw = apiError?.body?.message;
        const msgs = Array.isArray(raw)
          ? raw
          : typeof raw === 'string'
            ? [raw]
            : [];

        let errorKey: string | undefined;

        if (
          (apiError.status === 400 || apiError.body?.statusCode === 400) &&
          msgs.some(m => /externalCode/i.test(m))
        ) {
          if (msgs.some(m => /less than 1/i.test(m))) {
            errorKey = 'process-flow.data-capture.external-code-too-small';
          } else if (msgs.some(m => /greater than 999/i.test(m))) {
            errorKey = 'process-flow.data-capture.external-code-too-large';
          } else {
            errorKey = 'process-flow.data-capture.external-code-out-of-range';
          }
        }

        const errorMessage = apiError.body?.errorCode
          ? t(apiError.body.errorCode)
          : t(errorKey || 'process-flow.data-capture.create-error');

        openNotification(t('error'), errorMessage, 'error');
      }
    },
    [
      navigate,
      openNotification,
      processFlowId,
      selectedCompany,
      t,
      traceabilityPlanId,
      updateProcessBlock,
    ]
  );

  const save = useCallback(
    async (saveAndExit?: boolean) => {
      if (selectedForm) {
        const values = await form.validateFields();
        await handleFinish(values, saveAndExit);
        return;
      }
      const formflatFileIntegrationValues =
        await formflatFileIntegration.validateFields();
      await handleFinishFlatFileForm(
        formflatFileIntegrationValues,
        saveAndExit
      );
    },
    [
      form,
      formflatFileIntegration,
      handleFinish,
      handleFinishFlatFileForm,
      selectedForm,
    ]
  );

  useImperativeHandle(ref, () => ({ save }), [save]);

  const [radioToggleValue, setRadioToggleValue] = useState<ConfigurationTabs>(
    ConfigurationTabs.FormCapture
  );

  const onChangeRadioToggle = (value: ConfigurationTabs) => {
    setRadioToggleValue(value);
  };

  return (
    <>
      <DarwinSpin fullscreen spinning={isUpdateProcessBlockPending} />
      <DarwinFlex justify="space-between" align="center">
        <RadioToggle
          value={radioToggleValue}
          onChange={onChangeRadioToggle}
          options={[
            {
              value: ConfigurationTabs.FormCapture,
              label: t('process-flow.data-capture.radio-toggle.form-capture'),
            },
            {
              value: ConfigurationTabs.FlatFile,
              label: t('process-flow.data-capture.radio-toggle.flat-file'),
            },
          ]}
        />

        {radioToggleValue === ConfigurationTabs.FlatFile && (
          <ActionsBar
            processBlock={processBlock}
            traceabilityPlanId={traceabilityPlanId || ''}
            formId={formVersionId || ''}
            formName={processBlock?.processForm?.form_name || ''}
            processMapName={processBlock?.name || ''}
            processName={processBlock?.process.name || ''}
            environment={processBlock?.status || ''}
            dataSourceId={dataSourceSelectedId || ''}
            showUpload={false}
            showDownload={false}
          />
        )}
      </DarwinFlex>

      <DarwinDivider className={style.formContainer} />

      {radioToggleValue === ConfigurationTabs.FlatFile && (
        <FlatFile
          form={formflatFileIntegration}
          initialValues={{
            formflatFileIntegration: '',
            externalCode: processBlock?.externalCode?.toString() || '',
          }}
          fields={formIntegrationFields}
          renderInput={renderInput}
          className={styles.dataForm}
        />
      )}

      {radioToggleValue === ConfigurationTabs.FormCapture && (
        <FormCapture
          form={form}
          initialValues={{
            form: templateId!,
            formVersion: formVersionId!,
            userIds: defaultUserIds,
          }}
          fields={formConfigurationFields}
          renderInput={renderInput}
          className={styles.dataForm}
          onFinish={handleFinish}
          captureFields={captureFields}
          isLoadingFields={isLoadingFields}
          showTable={showTable}
          isInitialProcess={dataFields?.is_initial_process || false}
          onFieldChange={onFieldChange}
        />
      )}
    </>
  );
});
